// The learner's practical exercise: instructions, what the submission must
// carry (evidence link / score with the minimum), the current submission
// status (pending · awaiting validation · validated · rejected + note) and the
// submit / resubmit form (POST /assignments/{id}/practice). Client-side hints
// only stop an obviously incomplete post; the server's 400 is shown inline.
// A system-verified exercise (Phase 6) has no form: the platform validates it
// when the matching event is recorded for the learner, so only the status,
// the validation note and "verified automatically when you …" are shown.
import { useT } from '../lib/i18n';
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { ClipboardList, ExternalLink, Loader2 } from 'lucide-react';
import { Button } from '@/features/shared/components/ui/button';
import { Input } from '@/features/shared/components/ui/input';
import { Label } from '@/features/shared/components/ui/label';
import { Textarea } from '@/features/shared/components/ui/textarea';
import { getApiErrorMessage } from '@/lib/apiError';
import type { CompletionView, EvidenceInput } from '@/services/learningService';
import { useSubmitPractice, useSystemEvents } from '../api/hooks';
import { SUBMISSION_STATUS_CLS, submissionStatusLabel, systemEventLabel, verificationHelp, verificationLabel } from '../lib/completion';

interface Props {
  view: CompletionView;
  /** The viewer owns the assignment (only the learner may submit). */
  canSubmit: boolean;
  /** learning:manage | learning:assign — may read the event catalog (labels); learners see the event type humanized. */
  canReadCatalog?: boolean;
  className?: string;
  idPrefix?: string;
}

export const PracticePanel: React.FC<Props> = ({ view, canSubmit, canReadCatalog, className, idPrefix = 'pp' }) => {
  const t = useT();
  const submit = useSubmitPractice();
  const practice = view.practice;
  const sub = view.submission;
  const isSystem = practice?.verification_kind === 'system';
  const events = useSystemEvents(!!canReadCatalog && isSystem);
  const [evidence, setEvidence] = useState('');
  const [note, setNote] = useState('');
  const [score, setScore] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  if (!practice) return null;

  const status = sub?.status || 'pending';
  const canPost = canSubmit && !isSystem && view.status !== 'cancelled' && (status === 'pending' || status === 'rejected');
  const showForm = canPost && (status === 'rejected' ? open : true);
  const action = systemEventLabel(events.data, practice.system_rule?.event_type);

  const post = () => {
    setErr(null);
    const ev = evidence.trim();
    const sc = score.trim() === '' ? undefined : Number(score);
    if (practice.evidence_required && !ev) return setErr(t('training:practice_err_evidence', 'This exercise needs an evidence link.'));
    if (practice.score_required && (sc === undefined || !Number.isFinite(sc))) return setErr(t('training:practice_err_score', 'This exercise needs a score.'));
    if (sc !== undefined && (!Number.isFinite(sc) || sc < 0 || sc > 100)) return setErr(t('training:practice_err_score_range', 'Score must be between 0 and 100.'));
    const body: EvidenceInput = {};
    if (ev) body.evidence_url = ev;
    if (note.trim()) body.note = note.trim();
    if (sc !== undefined) body.score = sc;
    submit.mutate({ assignmentId: view.assignment_id, body, quiet: true }, {
      onSuccess: () => { toast.success(t('training:practice_submitted_ok', 'Exercise submitted')); setEvidence(''); setNote(''); setScore(''); setOpen(false); },
      onError: (e) => setErr(getApiErrorMessage(e, t('training:err', 'Something went wrong'))),
    });
  };

  return (
    <section className={`space-y-2 ${className || ''}`} data-testid="practice-panel">
      <div className="flex flex-wrap items-center gap-2">
        <h4 className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground"><ClipboardList className="h-3.5 w-3.5" aria-hidden /> {t('training:practice_title', 'Practical exercise')}</h4>
        <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${SUBMISSION_STATUS_CLS[status] || SUBMISSION_STATUS_CLS.pending}`} data-testid="submission-status" data-status={status}>{submissionStatusLabel(t, status)}</span>
      </div>
      <div className="font-medium text-foreground">{practice.title}</div>
      {practice.instructions && <p className="whitespace-pre-line text-sm text-foreground">{practice.instructions}</p>}
      <ul className="text-[11px] text-muted-foreground space-y-0.5" data-testid="practice-hints">
        {!isSystem && <li>{practice.evidence_required ? t('training:practice_hint_evidence', 'Evidence link required') : t('training:practice_hint_evidence_opt', 'Evidence link optional')}</li>}
        {!isSystem && <li>{practice.score_required
          ? (practice.min_score != null ? t('training:practice_hint_score_min', 'Score required · minimum {{n}}', { n: practice.min_score }) : t('training:practice_hint_score', 'Score required'))
          : t('training:practice_hint_score_opt', 'Score optional')}</li>}
        <li>{verificationLabel(t, practice.verification_kind)} — {verificationHelp(t, practice.verification_kind)}</li>
      </ul>
      {isSystem && (
        <p className="rounded-md border border-border bg-muted/40 p-2 text-xs text-foreground" data-testid="practice-system-notice">
          {action
            ? t('training:practice_system_notice', 'This exercise is verified automatically when you do this in KaziPeople: {{action}}.', { action })
            : t('training:practice_system_notice_generic', 'This exercise is verified automatically by your activity in KaziPeople.')}
        </p>
      )}

      {sub && status !== 'pending' && (
        <div className="rounded-md border border-border p-2 text-xs space-y-0.5" data-testid="submission-summary">
          {sub.submitted_at && <div className="text-muted-foreground">{t('training:submitted_at', 'Submitted {{when}}', { when: new Date(sub.submitted_at).toLocaleString() })}</div>}
          <div className="flex flex-wrap items-center gap-2">
            {sub.evidence_url && <a className="inline-flex items-center gap-1 text-orange-600 hover:underline" href={sub.evidence_url} target="_blank" rel="noreferrer">{t('training:view_evidence', 'View evidence')} <ExternalLink className="h-3 w-3" aria-hidden /></a>}
            {sub.score != null && <span className="text-foreground">{t('training:score_n', 'Score: {{n}}', { n: sub.score })}</span>}
          </div>
          {sub.note && <div className="text-muted-foreground">{t('training:your_note', 'Your note')}: {sub.note}</div>}
          {(status === 'validated' || status === 'rejected') && (
            <div className={status === 'rejected' ? 'text-red-700 dark:text-red-300' : 'text-success-strong'} data-testid="validation-note">
              {status === 'rejected' ? t('training:rejected_by', 'Rejected') : t('training:validated_by', 'Validated')}{sub.validated_by ? ` · ${sub.validated_by}` : ''}{sub.validated_at ? ` · ${new Date(sub.validated_at).toLocaleString()}` : ''}
              {sub.validation_note ? ` — ${sub.validation_note}` : ''}
            </div>
          )}
          {status === 'submitted' && <div className="text-muted-foreground">{t('training:awaiting_validation_help', 'Waiting for {{who}}.', { who: verificationLabel(t, sub.verification_kind).toLowerCase() })}</div>}
        </div>
      )}

      {canPost && status === 'rejected' && !open && (
        <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="practice-resubmit">{t('training:practice_resubmit', 'Resubmit')}</Button>
      )}
      {showForm && (
        <div className="space-y-2 rounded-md border border-orange-300 bg-orange-50 dark:bg-orange-500/10 p-2" data-testid="practice-form">
          <div className="space-y-1">
            <Label htmlFor={`${idPrefix}-evidence`} className="text-xs">{practice.evidence_required ? t('training:evidence_required_label', 'Evidence link') : t('training:evidence', 'Evidence link (optional)')}</Label>
            <Input id={`${idPrefix}-evidence`} className="h-8" value={evidence} onChange={(e) => setEvidence(e.target.value)} placeholder="https://…" />
          </div>
          {practice.score_required && (
            <div className="space-y-1">
              <Label htmlFor={`${idPrefix}-score`} className="text-xs">{t('training:score_label', 'Score (0–100)')}</Label>
              <Input id={`${idPrefix}-score`} className="h-8 w-28" type="number" min={0} max={100} value={score} onChange={(e) => setScore(e.target.value)} />
            </div>
          )}
          <div className="space-y-1">
            <Label htmlFor={`${idPrefix}-note`} className="text-xs">{t('training:note_optional', 'Note (optional)')}</Label>
            <Textarea id={`${idPrefix}-note`} rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
          {err && <div role="alert" className="text-xs text-red-600 dark:text-red-400">{err}</div>}
          <div className="flex justify-end gap-2">
            {status === 'rejected' && <Button size="sm" variant="ghost" onClick={() => setOpen(false)} disabled={submit.isPending}>{t('training:cancel', 'Cancel')}</Button>}
            <Button size="sm" disabled={submit.isPending} onClick={post} data-testid="practice-submit">
              {submit.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : t('training:practice_submit', 'Submit exercise')}
            </Button>
          </div>
        </div>
      )}
      {!canSubmit && !isSystem && status === 'pending' && <p className="text-[11px] text-muted-foreground">{t('training:practice_not_submitted_yet', 'The learner has not submitted this exercise yet.')}</p>}
    </section>
  );
};

export default PracticePanel;
